import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'

const LoanApplicationPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [finsireLink, setFinsireLink] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Get the Finsire link from navigation state
  useEffect(() => {
    const state = location.state as { finsireLink?: string }
    if (state?.finsireLink) {
      setFinsireLink(state.finsireLink)
      setIsLoading(false)
    } else {
      setError('No application link provided')
      setIsLoading(false)
    }
  }, [location.state])

  const handleBack = () => {
    navigate('/')
  }

  const handleClose = () => {
    navigate('/')
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <button
            onClick={handleBack}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-700" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Loan Application</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Error Content */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Application Error</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleBack}
              className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Go Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm">
        <button
          onClick={handleBack}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-700" />
        </button>
        <h2 className="text-lg font-semibold text-gray-900">Loan Application</h2>
        <button
          onClick={handleClose}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <XMarkIcon className="w-6 h-6 text-gray-700" />
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading your application...</p>
          </div>
        </div>
      )}

      {/* Application Content */}
      {!isLoading && finsireLink && (
        <div className="flex-1">
          <iframe
            src={finsireLink}
            className="w-full h-full border-0"
            title="Loan Application"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation allow-downloads"
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setError('Failed to load the application. Please try again.')
              setIsLoading(false)
            }}
          />
        </div>
      )}

      {/* User Info Bar (Optional) */}
      <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Applying as: {user?.full_name || 'User'}</span>
          <span>Phone: {user?.phone_number}</span>
        </div>
      </div>
    </div>
  )
}

export default LoanApplicationPage
